import { refresh } from '@components/FieldsList/utils'
import { onUnmounted } from 'vue'

import useUpdateTheme from './useUpdateTheme'

import type { IListenerCondition } from '@/types/serverField.type'

import { FieldClass, SectionsClass } from '@/api/fields'
import { formatDateHuman } from '@/api/formatter'
import { EnumEventTypes } from '@/constants/eventTypes.constants'
import { fullNameCases } from '@/utils/nameCases/russianNameCases'

type LegacyListener = {
  FieldsNames?: string[]
  CallbackType?: string
  Params?: Record<string, any>
  Conditions?: IListenerCondition[] | null
  Callbacks?: string[] | null
}

type FieldEvent = CustomEvent<{ field: any, silent?: boolean }>

const setSectionVisibleIfTrue = (listener: LegacyListener, field: FieldClass, setSectionVisible: (index: number, visible: boolean) => void) => {
  const index = listener.Params?.SectionIndex
  if (typeof index !== 'number') return
  const value = typeof field.value === 'boolean' ? field.value : field.value === 'true'
  setSectionVisible(index, value)
}

const forwardWidgetViewFilterFieldValueHandler = (field: any, listener: LegacyListener, value: any) => {
  const fieldTo = listener.Params?.FieldTo
  if (!fieldTo) return

  // Прокинем в viewFilterFieldFromField имя поля, с которого нужно забрать значение
  field.options.viewFilterFieldFromField = field.options.viewFilterFieldFromField || {}
  field.options.viewFilterFieldFromField[fieldTo] = fieldTo

  if (!value) return

  field._widget.beforeGetData = (_field: any) => {
    // TODO: value.value || value = костыль для gorn-702, потому что в value может содержаться объект.
    _field._widget.filterFields.getFieldByName(fieldTo).fieldValue = value.value ?? value
  }
}

// Значение некоторых полей представлено объектом вида { value: ... }.
// Для реакций сравниваем его так же, как обычное скалярное значение.
const getComparableValue = (value: unknown): unknown => {
  if (typeof value === 'object' && value !== null && 'value' in value) {
    return value.value
  }

  return value
}

const valuesAreEqual = (value: unknown, expectedValue: unknown): boolean =>
  String(getComparableValue(value)) === String(getComparableValue(expectedValue))

// Для операторов >, >=, <, <= по возможности приводим значения к числам.
// Если числовое преобразование невозможно, сравниваем их строковые представления.
const getRelationalValue = (value: unknown): number | string => {
  const comparableValue = getComparableValue(value)
  const numericValue = typeof comparableValue === 'string' && comparableValue.trim() === ''
    ? Number.NaN
    : Number(comparableValue)

  return Number.isFinite(numericValue) ? numericValue : String(comparableValue)
}

// Проверяет одно условие listener-а по текущему значению указанного поля.
export const listenerConditionMatches = (condition: IListenerCondition, sections: SectionsClass) => {
  const conditionField = sections.getFieldByName(condition.FieldName)
  if (!conditionField) return false

  const actualValue = conditionField.getCurrentValue()

  switch (condition.Operator.trim()) {
    case '==':
      return valuesAreEqual(actualValue, condition.Value)
    case '!=':
      return !valuesAreEqual(actualValue, condition.Value)
    case '>':
      return getRelationalValue(actualValue) > getRelationalValue(condition.Value)
    case '>=':
      return getRelationalValue(actualValue) >= getRelationalValue(condition.Value)
    case '<':
      return getRelationalValue(actualValue) < getRelationalValue(condition.Value)
    case '<=':
      return getRelationalValue(actualValue) <= getRelationalValue(condition.Value)
    default:
      return false
  }
}

// Последовательно объединяет результаты условий через AND/OR.
// LogicalOperator относится к текущему условию и связывает его с накопленным результатом.
export const listenerConditionsMatch = (conditions: IListenerCondition[] | null | undefined, sections: SectionsClass) => {
  if (!conditions?.length) return false

  return conditions.reduce((result, condition, index) => {
    const conditionResult = listenerConditionMatches(condition, sections)
    if (index === 0) return conditionResult

    return condition.LogicalOperator.trim().toLowerCase() === 'or'
      ? result || conditionResult
      : result && conditionResult
  }, false)
}

// readonly и _disabled должны изменяться вместе, чтобы поле нельзя было
// редактировать как через компонент, так и через его внутреннюю логику.
const setReadonly = (field: FieldClass, readonly: boolean) => {
  field.readonly = readonly
  field._disabled = readonly
}

const valueMatchesListenerParam = (listener: LegacyListener, value: unknown): boolean => {
  const params = listener.Params || {}
  return Object.prototype.hasOwnProperty.call(params, 'Value') &&
    valuesAreEqual(value, params.Value)
}

// Делает поле обязательным, когда значение слушаемого поля совпадает с Params.Value.
const setRequiredWhenValueIs = (field: FieldClass, listener: LegacyListener, value: unknown) => {
  field.required = valueMatchesListenerParam(listener, value)
}

// Блокирует поле, когда значение слушаемого поля совпадает с Params.Value.
const setReadonlyWhenValueIs = (field: FieldClass, listener: LegacyListener, value: unknown) => {
  setReadonly(field, valueMatchesListenerParam(listener, value))
}

// Блокирует поле, когда значение слушаемого поля не совпадает с Params.Value.
const setReadonlyWhenValueNotIs = (field: FieldClass, listener: LegacyListener, value: unknown) => {
  setReadonly(field, !valueMatchesListenerParam(listener, value))
}

// Храним исходное описание, чтобы восстановить его, когда значение
// слушаемого поля перестанет соответствовать одному из ключей Params.
const initialDescriptions = new WeakMap<FieldClass, string>()

// Params содержит соответствие значений слушаемого поля новым описаниям:
// { "значение": "Новое описание" }.
const setDescriptionWhenValueIs = (field: FieldClass, listener: LegacyListener, value: unknown) => {
  if (!initialDescriptions.has(field)) {
    initialDescriptions.set(field, field.description)
  }

  const descriptions = listener.Params
  const comparableValue = String(getComparableValue(value))

  field.description = descriptions && Object.prototype.hasOwnProperty.call(descriptions, comparableValue)
    ? descriptions[comparableValue]
    : initialDescriptions.get(field) ?? ''
}

const setFormSectionIdAndWeight = (field: FieldClass, listener: LegacyListener, key: unknown, sections: SectionsClass) => {
  if (String(key) !== String(listener.Params?.Value)) return

  const allSections = sections.getAllSections()

  for (const section of allSections) {
    const idx = section.fields.indexOf(field)
    if (idx !== -1) {
      section.fields.splice(idx, 1)
      break
    }
  }

  if (listener.Params?.Weight) {
    field.weight = listener.Params.Weight
  }

  const targetSection = allSections.find(s => s.id === listener.Params?.NewSectionId)
  if (targetSection) {
    const pos = targetSection.fields.findIndex(f => f.weight >= field.weight)
    targetSection.fields.splice(pos === -1 ? targetSection.fields.length : pos, 0, field)
  }
}

const dispatchVisibleField = (field: FieldClass) => {
  if (field.visible) {
    field.dispatch(EnumEventTypes.INIT, { silent: true })
  }
}

export function useEventListeners (
  sections: SectionsClass,
  field: FieldClass,
  config: Record<string, unknown>,
  setSectionVisible: (index: number, visible: boolean) => void,
  uid: number,
  autoCleanup = true
) {
  let updateThemeTimeout: ReturnType<typeof setTimeout> | null = null
  const registeredListeners: Array<{ name: string, handler: EventListener }> = []

  const handleEvent = (evt?: FieldEvent, listener?: LegacyListener) => {
    // Поле, которое слушают
    const targetField = evt?.detail?.field

    // Скрытое поле не слушаем (gorn-679)
    if (!(targetField && targetField.visible === true)) return false

    const currentValue = targetField.getCurrentValue()
    const key = (typeof currentValue === 'object' && currentValue !== null && 'value' in currentValue)
      ? currentValue.value
      : currentValue
    let value = currentValue

    if (Array.isArray(value)) {
      value = Boolean(value.length)
    }

    switch (listener?.CallbackType) {
      case 'refreshFieldData':
        if (!evt.detail.silent) {
          refreshFieldData(evt, listener)
        }
        break
      case 'refreshFields':
        refreshFields(evt, listener)

        break
      case 'setSectionVisibleIfTrue':
        setSectionVisibleIfTrue(listener, field, setSectionVisible)
        break
      case 'setVisibleOnMapKeyGt0':
        field.visible = Number(key) > 0
        field.readonly = !field.visible

        dispatchVisibleField(field)
        break
      case 'setVisibleAndResetValueOnMapKeyGt0':
        field.visible = Number(key) > 0
        if (!field.visible) {
          field.value = field.prepareValue(field.defaultValue)
        }
        dispatchVisibleField(field)
        break
      case 'setVisibleOnMapKeyEq0':
        field.visible = Number(key) === 0
        field.readonly = !field.visible
        dispatchVisibleField(field)
        break
      case 'setRequiredOnMapKeyEq0':
        field.required = Number(key) === 0
        break
      case 'setVisibleAndRequiredOnValueIsTrue':
        field.visible = value
        field.required = value
        field.readonly = !value
        dispatchVisibleField(field)
        break
      case 'setRequiredOnValueIsTrue':
        field.required = value
        break
      case 'setVisibleOnValueIsTrue':
        field.visible = value
        dispatchVisibleField(field)
        break
      case 'themeUpdate':
        if (!evt.detail.silent) {
          if (updateThemeTimeout) {
            clearTimeout(updateThemeTimeout)
          }

          updateThemeTimeout = setTimeout(() => {
            ;(listener.FieldsNames || []).forEach((fieldName: string) => {
              const value = sections.getFieldByName(fieldName)?.value
              useUpdateTheme(fieldName, value == null ? '' : String(value))
            })
          }, 250)
        }
        break
      case 'setVisibleRequiredOnValueIsTrue_v2':
        field.visible = value
        field.required = value
        dispatchVisibleField(field)
        break
      case 'setVisibleAndReadOnlyOnValueIsTrue':
        field.visible = value
        field.readonly = !value
        dispatchVisibleField(field)
        break
      case 'setCheckedOnDateSet':
        field.value = formatDateHuman(value) !== ''
        break
      case 'clear':
        if (!evt.detail.silent) {
          field.clear()
          field.dispatch(EnumEventTypes.CHANGE)
        }
        break
      case 'forwardWidgetViewFilterFieldValue':
        forwardWidgetViewFilterFieldValueHandler(field, listener, value)
        break
      case 'setRequiredWhenValueIs':
        setRequiredWhenValueIs(field, listener, value)
        break
      case 'setReadonlyWhenValueIs':
        setReadonlyWhenValueIs(field, listener, value)
        break
      case 'setReadonlyWhenValueNotIs':
        setReadonlyWhenValueNotIs(field, listener, value)
        break
      case 'setDescriptionWhenValueIs':
        setDescriptionWhenValueIs(field, listener, value)
        break
      case 'setFormSectionIdAndWeight': {
        setFormSectionIdAndWeight(field, listener, key, sections)
        break
      }
      case 'createCases': {
        const sourceFieldName = listener.Params?.Source
        const caseKey = listener.Params?.Case
        if (!sourceFieldName || !caseKey) {
          console.error('createCases: отсутствует параметр Source или Case в listener.Params')
          break
        }
        const sourceField = sections.getFieldByName(sourceFieldName)
        if (!sourceField) {
          console.error(`createCases: поле-источник "${sourceFieldName}" не найден`)
          break
        }
        const cases = fullNameCases(String(sourceField.value ?? ''))
        field.value = cases[caseKey as keyof typeof cases] ?? field.value
        break
      }
      case 'setDisabledOnValueIsEmpty': {
        if (targetField.isEmpty instanceof Function) {
          field._disabled = targetField.isEmpty()
        }
        break
      }
      case 'useConditions': {
        if (!listenerConditionsMatch(listener.Conditions, sections)) break

        ;(listener.Callbacks || []).forEach(callbackType => {
          if (callbackType === 'useConditions') {
            console.error('useConditions не может вызывать сам себя')
            return
          }

          handleEvent(evt, { ...listener, CallbackType: callbackType })
        })
        break
      }
      default:
        console.error('Нет обработчика для события ' + listener?.CallbackType)
        break
    }
  }

  const refreshFieldData = async (evt: FieldEvent, listener: LegacyListener) => {
    const _fields = await refresh(field as FieldClass, evt.detail?.field, config, listener, sections)
    const _preparedFields = Array.isArray(_fields) ? _fields : [_fields]

    _preparedFields.forEach(_field => {
      if (!_field) return

      if (_field.name !== field.name) return

      field.refreshValue(_field)
    })
  }

  const refreshFields = async (evt: FieldEvent, listener: LegacyListener) => {
    if (!(listener.Params && listener.Params.FieldsToRefresh)) {
      return
    }

    const _fields = await refresh(field as FieldClass, evt.detail?.field, config, listener, sections)
    const _preparedFields = Array.isArray(_fields) ? _fields : [_fields]

    _preparedFields.forEach(_field => {
      if (!_field) return

      // Найдем поле, которое нужно обновить
      const targetField = sections.getFieldByName(_field.name)
      if (targetField) {
        targetField.refreshField(_field)
      }
    })
  }

  const addListeners = () => {
    ;(field.listeners || []).forEach(listener => {
      const event = listener.EventType + '-' + uid + '-';
      (listener.FieldsNames || []).forEach(fieldName => {
        const name = event + fieldName
        const handler = (evt: Event) => handleEvent(evt as FieldEvent, listener as LegacyListener)

        document.addEventListener(name, handler)
        registeredListeners.push({ name, handler })
      })
    })
  }

  const removeListeners = () => {
    registeredListeners.forEach(({ name, handler }) => {
      document.removeEventListener(name, handler)
    })
    registeredListeners.length = 0
    if (updateThemeTimeout) clearTimeout(updateThemeTimeout)
  }

  addListeners()

  if (autoCleanup) onUnmounted(removeListeners)

  return {
    removeListeners
  }
}
